import React from 'react';
import * as ReactDOM from 'react-dom/client';
import { convertUnit } from '../common/units';
import { 
    DialogWrapper, SvgAnnotations, ImportSvgResolverDialog, 
    EditorReactHost
} from '../components';
import { blazorServiceInstance, svgEditServiceInstance, panelsServiceInstance, zenDataServiceInstance } from '@/services';
import { BoundingBoxZoomOption } from '../svgcanvas/elem-get-set';
import { XYObject } from '../svgcanvas/math';
import SvgCanvas from '../svgcanvas/svgcanvas';
import ConfigObj, { ISVGEditConfiguration } from './ConfigObj';
import { seAlert } from './dialogs/seAlertDialog';
import { seConfirm } from './dialogs/seConfirmDialog';
import { putLocale } from './locale.js';
import { TopPanelLegacy } from '@/components/panels/TopPanel';
import { BLAZOR_ASSEMBLY } from "@/common/constants";
import { isElementInLockedLayer, isElementEffectivelyLocked } from '@/svgcanvas/layer-tools';
import Rulers from './Rulers';
import ShortcutManager, { shortcutCategories } from './ShortcutManager';
import { builtInExtensions } from './built-in-extensions';
import './touch.ts';

const { $id, $qa, isNullish, decode64, blankPageObjectURL } = SvgCanvas;

const IMPORT_ARTWORK_INPUT_ID = "pd-input-importartworkfromcomputer";

export interface IKeyboardShortcut {
    key: string | [string] | [string, boolean];
    fn: () => void;
}

/**
   * @fires module:svgcanvas.SvgCanvas#event:svgEditorReady
   * @returns {void}
   */
const readySignal = () => {
    // let the opener know SVG Edit is ready (now that config is set up)
    const w = window.opener || window.parent;
    if (w) {
        try {
            /**
               * Triggered on a containing `document` (of `window.opener`
               * or `window.parent`) when the editor is loaded.
               * @event module:SVGEditor#event:svgEditorReadyEvent
               * @type {Event}
               * @property {true} bubbles
               * @property {true} cancelable
               */
            /**
               * @name module:SVGthis.svgEditorReadyEvent
               * @type {module:SVGEditor#event:svgEditorReadyEvent}
               */
            const svgEditorReadyEvent = new w.CustomEvent('svgEditorReady', {
                bubbles: true,
                cancelable: true
            });
            w.document.documentElement.dispatchEvent(svgEditorReadyEvent);
        } catch (e) {/* empty fn */ }
    }
};

interface ReactHostRef {
    elementID: string;
    reactElement: React.ReactNode;
}

function createReactHostedElement(h: ReactHostRef) {
    const el = document.getElementById(h.elementID);
    if(!el) {
        console.warn(`Could not find element with ID '${h.elementID}' to rehook React element.`);
        return;
    }

    if(el.hasAttribute("react-host")) {
        // Blazor hot reload hasn't broken this element, so we're safe to abort.
        return;
    }

    if(!el.hasAttribute("react-host-enabled")) {
        // Blazor has disabled using react for this control, so abort.
        return;
    }

    const root = ReactDOM.createRoot(el);
    root.render(h.reactElement);
    el.setAttribute("react-host", "true");
}

const reactHosts: ReactHostRef[] = [];
function rehookReactHosts() {
    for(const h of reactHosts) {
        createReactHostedElement(h);
    }
}

setInterval(rehookReactHosts, 1000);

function registerReactHost(elementID: string, reactElement: React.ReactNode) {
    const h = {
        elementID,
        reactElement,
    };

    reactHosts.push(h);
    createReactHostedElement(h);
}

/**
 *
 */
export default class Editor {
    public extensionsAdded = false;
    public messageQueue = [];
    public $svgEditor: HTMLElement;
    public configObj: ConfigObj;
    public goodLangs;
    public i18next;
    public svgCanvas: SvgCanvas;

    public topPanel: TopPanelLegacy;
    public rulers: Rulers;

    public workarea: HTMLElement;
    public exportWindow: Window = null;
    public defaultImageURL: string;
    public uiContext: string;

    public selectedElement: SVGElement;
    public multiselected: boolean = false;

    public title: string;

    private lastX: number | null = null;
    private lastY: number | null = null;
    private panning: boolean = false;
    private keypan: boolean = false;

    private currentFileUrl: string | null = null;

    public langChanged = false;
    public showSaveWarning = false;
    public isReady = false;
    public customExportImage: false | ((...args: any[]) => any) = false;
    public customExportPDF: false | ((...args: any[]) => any) = false;
    public callbacks: any[] = [];
    public curContext = null;
    public exportWindowName: string | null = null;
    public docprops = false;
    public shortcuts: IKeyboardShortcut[] = [];
    public uiStrings;

    /**
     *
     */
    static Create(dotNetServiceRef: any) {
        blazorServiceInstance.Initialize(dotNetServiceRef);

        return new Promise<Editor>((resolve, reject) => {
            const editor = new Editor();
            const attemptLimit = 100;

            function initialConstruct(attempt: number = 0) {
                const svgEditorElement = $id('svg_editor');
                if(!svgEditorElement) {
                    if(attempt >= attemptLimit) {
                        reject(new Error(`Could not construct SvgEditor instanced after ${attemptLimit} attempts - No element with ID 'svg_editor' could be found.`));
                        return;
                    }

                    setTimeout(() => initialConstruct(++attempt), 100);
                    return;
                }

                editor.$svgEditor = svgEditorElement;
                window['svgEditor'] = editor;

                registerReactHost("react_misc_host", <EditorReactHost />);

                svgEditServiceInstance.InitializeEditor(editor);

                editor.svgCanvas = null;
                editor.configObj = new ConfigObj(editor);
                editor.configObj.pref = editor.configObj.pref.bind(editor.configObj);
                editor.configObj.preferences = false;
                editor.setConfig = editor.configObj.setConfig.bind(editor.configObj);
                // eslint-disable-next-line max-len
                editor.goodLangs = ['en'];
                ShortcutManager.Initialize();

                editor.topPanel = new TopPanelLegacy(editor);
                setTimeout(() => { // 1 tick delay for everything to settle.
                    editor.init();
                });
                resolve(editor);
            }

            initialConstruct(0);
        });
    }

    /**
    * Auto-run after a Promise microtask.
    * @function module:SVGthis.init
    * @returns {void}
    */
    private async init() {
        const self = this;
        if (!this.i18next) {
            const { i18next } = await putLocale(this.configObj.pref('lang'), this.goodLangs);
            this.i18next = i18next;
        }

        zenDataServiceInstance.Initialize();
        this.bindShortcuts();

        // allow to prepare the dom without display
        this.$svgEditor.style.visibility = 'hidden';
        try {
            const textInputProxy = document.createElement('input');
            textInputProxy.setAttribute("id", "text_input_el");
            textInputProxy.setAttribute("type", "text");
            document.body.append(textInputProxy);

            // Image props dialog added to DOM
            const newSeImgPropDialog = document.createElement('se-img-prop-dialog');
            newSeImgPropDialog.setAttribute('id', 'se-img-prop');
            document.body.append(newSeImgPropDialog);
            // editor prefences dialoag added to DOM
            const newSeEditPrefsDialog = document.createElement('se-edit-prefs-dialog');
            newSeEditPrefsDialog.setAttribute('id', 'se-edit-prefs');
            document.body.append(newSeEditPrefsDialog);
            // alertDialog added to DOM
            const alertBox = document.createElement('se-alert-dialog');
            alertBox.setAttribute('id', 'se-alert-dialog');
            document.body.append(alertBox);
            // promptDialog added to DOM
            const promptBox = document.createElement('se-prompt-dialog');
            promptBox.setAttribute('id', 'se-prompt-dialog');
            document.body.append(promptBox);
            // Export dialog added to DOM
            const exportDialog = document.createElement('se-export-dialog');
            exportDialog.setAttribute('id', 'se-export-dialog');
            document.body.append(exportDialog);
        } catch (err) {
            console.error(err);
        }

        this.configObj.load();

        /**
        * @name module:SVGthis.canvas
        * @type {module:svgcanvas.SvgCanvas}
        */
        this.svgCanvas = new SvgCanvas(
            $id('svgcanvas'),
            this.configObj.curConfig
        );
        this.svgCanvas.editor = this;

        this.topPanel.init();

        const { undoMgr } = this.svgCanvas;
        this.workarea = document.getElementById('workarea');
        this.exportWindow = null;
        this.defaultImageURL = this.configObj.curConfig.imgPath + 'logo.svg';
        this.uiContext = 'toolbars';

        // For external openers
        readySignal();

        this.rulers = new Rulers(this);

        this.selectedElement = null;
        this.multiselected = false;

        // bind the selected event to our function that handles updates to the UI
        this.svgCanvas.bind('selected', this.selectedChanged.bind(this));
        this.svgCanvas.bind('transition', this.elementTransition.bind(this));
        this.svgCanvas.bind('changed', this.elementChanged.bind(this));
        this.svgCanvas.bind('exported', this.exportHandler.bind(this));
        this.svgCanvas.bind('exportedPDF', (win, data) => {
            if (!data.output) { // Ignore Chrome
                return;
            }
            const { exportWindowName } = data;
            if (exportWindowName) {
                this.exportWindow = window.open('', this.exportWindowName); // A hack to get the window via JSON-able name without opening a new one
            }
            if (!this.exportWindow || this.exportWindow.closed) {
                seAlert(this.i18next.t('notification.popupWindowBlocked'));
                return;
            }
            this.exportWindow.location.href = data.output;
        });
        this.svgCanvas.bind('zoomed', (_, bbox, autoCenter) => this.zoomChanged(bbox, autoCenter));
        this.svgCanvas.bind('zoomDone', this.zoomDone.bind(this));
        this.svgCanvas.bind(
            'updateCanvas',
            /**
           * @param {external:Window} win
           * @param {PlainObject} centerInfo
           * @param {false} centerInfo.center
           * @param {module:math.XYObject} centerInfo.newCtr
           * @listens module:svgcanvas.SvgCanvas#event:updateCanvas
           * @returns {void}
           */
            (win, { center, newCtr }) => {
                this.updateCanvas(center, newCtr);
            }
        );
        this.svgCanvas.bind('extension_added', this.extAdded.bind(this));
        this.svgCanvas.textActions.setInputElem($id('text_input_el'));

        this.setBackground(this.configObj.pref('bkgd_color'), this.configObj.pref('bkgd_url'));

        // update resolution option with actual resolution
        const res: {
            w: number | string,
            h: number | string
        } = this.svgCanvas.getResolution();

        if (this.configObj.curConfig.baseUnit !== 'px') {
            res.w = convertUnit(res.w) + this.configObj.curConfig.baseUnit;
            res.h = convertUnit(res.h) + this.configObj.curConfig.baseUnit;
        }
        $id('se-img-prop').setAttribute('dialog', 'close');
        $id('se-img-prop').setAttribute('title', this.svgCanvas.getDocumentTitle());
        $id('se-img-prop').setAttribute('width', res.w.toString());
        $id('se-img-prop').setAttribute('height', res.h.toString());
        $id('se-img-prop').setAttribute('save', this.configObj.pref('img_save'));

        // Lose focus for select elements when changed (Allows keyboard shortcuts to work better)
        const selElements = document.querySelectorAll("select");
        Array.from(selElements).forEach(function (element) {
            element.addEventListener('change', function (evt) {
                (evt.currentTarget as HTMLElement).blur();
            });
        });

        // fired when user wants to move elements to another layer
        /*
        let promptMoveLayerOnce = false;
        $id('selLayerNames').addEventListener('change', function (evt) {
            const evtTarget = evt.currentTarget as any; // TODO: Fix as any
            const destLayer = evtTarget.options[evtTarget.selectedIndex].value;
            const confirmStr = self.i18next.t('notification.QmoveElemsToLayer').replace('%s', destLayer);
            /**
          * @param {boolean} ok
          * @returns {void}
          * /
            const moveToLayer = (ok) => {
                if (!ok) { return; }
                promptMoveLayerOnce = true;
                self.svgCanvas.moveSelectedToLayer(destLayer);
                self.svgCanvas.clearSelection();
            };
            if (destLayer) {
                if (promptMoveLayerOnce) {
                    moveToLayer(true);
                } else {
                    const ok = seConfirm(confirmStr);
                    if (!ok) {
                        return;
                    }
                    moveToLayer(true);
                }
            }
        });
        */

        svgEditServiceInstance.InitializeCanvas(this.svgCanvas);


        function addListenerMulti(element, eventNames, listener) {
            let events = eventNames.split(' ');
            for (let i = 0, iLen = events.length; i < iLen; i++) {
                element.addEventListener(events[i], listener, false);
            }
        }

        addListenerMulti($id('text_input_el'), 'keyup input', (evt) => {
            this.svgCanvas.setTextContent(evt.currentTarget.value);
            SvgAnnotations.UpdateAnnotations();
        });

        $id('svgcanvas').addEventListener('mouseup', (evt) => {
            if (this.panning === false) {
                return true;
            }

            this.workarea.scrollLeft -= (evt.clientX - this.lastX);
            this.workarea.scrollTop -= (evt.clientY - this.lastY);

            this.lastX = evt.clientX;
            this.lastY = evt.clientY;

            if (evt.type === 'mouseup') {
                this.panning = false;
            }

            return false;
        });
        // eslint-disable-next-line sonarjs/no-identical-functions
        $id('svgcanvas').addEventListener('mousemove', (evt) => {
            if (this.panning === false) {
                return true;
            }

            this.workarea.scrollLeft -= (evt.clientX - this.lastX);
            this.workarea.scrollTop -= (evt.clientY - this.lastY);

            this.lastX = evt.clientX;
            this.lastY = evt.clientY;

            if (evt.type === 'mouseup') {
                this.panning = false;
            }

            return false;
        });
        $id('svgcanvas').addEventListener('mousedown', (evt) => {
            if (evt.button === 1 || this.keypan === true) {
                this.panning = true;
                this.lastX = evt.clientX;
                this.lastY = evt.clientY;
                return false;
            }

            return true;
        });

        window.addEventListener('mouseup', () => {
            this.panning = false;
        });

        document.addEventListener('keydown', (e) => {
            const target = e.target as HTMLElement;
            const eventKey = e.key?.toLowerCase();
            const nodeName = target.nodeName.toLowerCase();

            /*
            if (target.nodeName !== 'BODY') {
                return;
            }
            */

            if (e.key.toLowerCase() === 'shift') {
                document.body.setAttribute("data-shift", "true");
            }

            if (e.code.toLowerCase() === 'space' && nodeName != "input" && nodeName != "textarea" && !this.checkIfContentEditableHasFocus(target, eventKey)) {
                this.svgCanvas.inputState.spaceKeyDown = this.keypan = true;
                document.body.setAttribute("data-panning", "true");
                e.preventDefault();
            }
        });

        document.addEventListener('keyup', (e) => {
            const target = e.target as HTMLElement;
            const eventKey = e.key?.toLowerCase();

            /*
            if (target.nodeName !== 'BODY') {
                return;
            }
            */

            if (e.key.toLowerCase() === 'shift') {
                document.body.removeAttribute("data-shift");
            }

            if (e.code.toLowerCase() === 'space' && !this.checkIfContentEditableHasFocus(target, eventKey)) {
                this.svgCanvas.inputState.spaceKeyDown = this.keypan = false;
                document.body.removeAttribute("data-panning");
                e.preventDefault();
            }
        });


        let inp;
        /**
          *
          * @returns {void}
          */
        const unfocus = () => {
            inp.blur();
        };

        const liElems = document.getElementById('svg_editor').querySelectorAll('button, select, input:not(#text_input_el)');
        Array.prototype.forEach.call(liElems, function (el) {
            el.addEventListener("focus", (e) => {
                inp = e.currentTarget;
                self.uiContext = 'toolbars';
                self.workarea.addEventListener('mousedown', unfocus);
            });
            el.addEventListener("blur", () => {
                self.uiContext = 'canvas';
                self.workarea.removeEventListener('mousedown', unfocus);
                // Go back to selecting text if in textedit mode
                if (self.svgCanvas.getMode() === 'textedit') {
                    $id('text_input_el').focus();
                }
            });
        });
        // ref: https://stackoverflow.com/a/1038781
        function getWidth() {
            return Math.max(
                document.body.scrollWidth,
                document.documentElement.scrollWidth,
                document.body.offsetWidth,
                document.documentElement.offsetWidth,
                document.documentElement.clientWidth
            );
        }

        function getHeight() {
            return Math.max(
                document.body.scrollHeight,
                document.documentElement.scrollHeight,
                document.body.offsetHeight,
                document.documentElement.offsetHeight,
                document.documentElement.clientHeight
            );
        }
        const winWh = {
            width: getWidth(),
            height: getHeight()
        };

        window.addEventListener('resize', () => {
            Object.entries(winWh).forEach(([type, val]) => {
                const curval = (type === 'width') ? window.innerWidth - 15 : window.innerHeight;
                this.workarea['scroll' + (type === 'width' ? 'Left' : 'Top')] -= (curval - val) / 2;
                winWh[type] = curval;
            });
        });

        this.workarea.addEventListener('scroll', () => {
            // TODO: jQuery's scrollLeft/Top() wouldn't require a null check
            this.rulers.manageScroll();
        });

        const elements = document.getElementsByClassName("push_button");
        Array.from(elements).forEach(function (element) {
            element.addEventListener('mousedown', function (event) {
                const target = event.currentTarget as HTMLElement;
                if (!target.classList.contains('disabled')) {
                    target.classList.add('push_button_pressed')
                    target.classList.remove('push_button');
                }
            });
            element.addEventListener('mouseout', function (event) {
                const target = event.currentTarget as HTMLElement;
                target.classList.add('push_button')
                target.classList.remove('push_button_pressed');
            });
            element.addEventListener('mouseup', function (event) {
                const target = event.currentTarget as HTMLElement;
                target.classList.add('push_button')
                target.classList.remove('push_button_pressed');
            });
        });

        const centerCanvas = () => {
            // this centers the canvas vertically in the this.workarea (horizontal handled in CSS)
            this.workarea.style.lineHeight = this.workarea.style.height;
        };

        addListenerMulti(window, 'load resize', centerCanvas);

        // Prevent browser from erroneously repopulating fields
        const inputEles = document.querySelectorAll('input');
        Array.from(inputEles).forEach(function (inputEle) {
            inputEle.setAttribute('autocomplete', 'off');
        });
        const selectEles = document.querySelectorAll('select');
        Array.from(selectEles).forEach(function (inputEle) {
            inputEle.setAttribute('autocomplete', 'off');
        });

        // Select given tool
        this.ready(() => {
            const preTool = $id(`tool_${this.configObj.curConfig.initTool}`);
            const regTool = $id(this.configObj.curConfig.initTool);
            const selectTool = $id('tool_select');
            const $editDialog = $id('se-edit-prefs');

            if (preTool) {
                preTool.click();
            } else if (regTool) {
                regTool.click();
            } else {
                selectTool.click();
            }

            if (this.configObj.curConfig.wireframe) {
                $id('tool_wireframe').click();
            }

            const showRulers = this.configObj.getBoolPref("showRulers", true);
            $id('rulers').style.display = showRulers ? 'block' : 'none';

            if (showRulers) {
                $editDialog.setAttribute('showrulers', "true");
            }

            if (this.configObj.curConfig.baseUnit) {
                $editDialog.setAttribute('baseunit', this.configObj.curConfig.baseUnit);
            }

            if (this.configObj.curConfig.gridSnapping) {
                $editDialog.setAttribute('gridsnappingon', "true");
            }

            if (this.configObj.curConfig.snappingStep) {
                $editDialog.setAttribute('gridsnappingstep', this.configObj.curConfig.snappingStep?.toString());
            }

            if (this.configObj.curConfig.gridColor) {
                $editDialog.setAttribute('gridcolor', this.configObj.curConfig.gridColor);
            }
        });

        window.addEventListener('beforeunload', (e) => {
            // Suppress warning if page is empty
            if (undoMgr.getUndoStackSize() === 0) {
                this.showSaveWarning = false;
            }

            // showSaveWarning is set to 'false' when the page is saved.
            if (!this.configObj.curConfig.no_save_warning && this.showSaveWarning) {
                // Browser already asks question about closing the page
                e.returnValue = this.i18next.t('notification.unsavedChanges'); // Firefox needs this when beforeunload set by addEventListener (even though message is not used)
                return this.i18next.t('notification.unsavedChanges');
            }
            return true;
        });

        // Use HTML5 File API: http://www.w3.org/TR/FileAPI/
        // if browser has HTML5 File API support, then we will show the open menu item
        // and provide a file input to click. When that change event fires, it will
        // get the text contents of the file and send it to the canvas
        if (window.FileReader) {
            /**
            * @param {Event} e
            * @returns {void}
            */
            const editorObj = this;
            const importImage = function (e) {
                document.getElementById('se-prompt-dialog').title = editorObj.i18next.t('notification.loadingImage');
                e.stopPropagation();
                e.preventDefault();
                const file = (e.type === 'drop') ? e.dataTransfer.files[0] : this.files[0];
                if (!file) {
                    document.getElementById('se-prompt-dialog').setAttribute('close', true);
                    return;
                }

                if (!file.type.includes('image')) {
                    return;
                }
                // Detected an image
                // svg handling
                let reader;
                if (file.type.includes('svg')) {
                    reader = new FileReader();
                    reader.onloadend = function (ev) {
                        const newElement = editorObj.svgCanvas.importSvgString(ev.target.result);
                        editorObj.svgCanvas.ungroupSelectedElement();
                        editorObj.svgCanvas.ungroupSelectedElement();
                        editorObj.svgCanvas.groupSelectedElements();
                        editorObj.svgCanvas.alignSelectedElements('m', 'page');
                        editorObj.svgCanvas.alignSelectedElements('c', 'page');
                        // highlight imported element, otherwise we get strange empty selectbox
                        editorObj.svgCanvas.selectOnly([newElement]);
                        document.getElementById('se-prompt-dialog').setAttribute('close', true);
                    };
                    reader.readAsText(file);
                } else {
                    // bitmap handling
                    reader = new FileReader();
                    reader.onloadend = function ({ target: { result } }) {
                        /**
                        * Insert the new image until we know its dimensions.
                        * @param {Float} imageWidth
                        * @param {Float} imageHeight
                        * @returns {void}
                        */
                        const insertNewImage = function (imageWidth, imageHeight) {
                            const newImage = editorObj.svgCanvas.addSVGElementFromJson({
                                element: 'image',
                                attr: {
                                    x: 0,
                                    y: 0,
                                    width: imageWidth,
                                    height: imageHeight,
                                    id: editorObj.svgCanvas.getNextId(),
                                    style: 'pointer-events:inherit'
                                }
                            });
                            editorObj.svgCanvas.setHref(newImage, result);
                            editorObj.svgCanvas.selectOnly([newImage]);
                            editorObj.svgCanvas.alignSelectedElements('m', 'page');
                            editorObj.svgCanvas.alignSelectedElements('c', 'page');
                            document.getElementById('se-prompt-dialog').setAttribute('close', true);
                        };
                        // create dummy img so we know the default dimensions
                        let imgWidth = 100;
                        let imgHeight = 100;
                        const img = new Image();
                        img.style.opacity = "0";
                        img.addEventListener('load', () => {
                            imgWidth = img.offsetWidth || img.naturalWidth || img.width;
                            imgHeight = img.offsetHeight || img.naturalHeight || img.height;
                            insertNewImage(imgWidth, imgHeight);
                        });
                        img.src = result;
                    };
                    reader.readAsDataURL(file);
                }
            };

            this.workarea.addEventListener('dragenter', this.onDragEnter);
            this.workarea.addEventListener('dragover', this.onDragOver);
            this.workarea.addEventListener('dragleave', this.onDragLeave);
            this.workarea.addEventListener('drop', importImage);
            const imgImport = document.createElement('input');
            imgImport.type = "file";
            imgImport.addEventListener('change', importImage);
            window.addEventListener('importImages', () => imgImport.click());
        }

        this.updateCanvas(true);
        // Load extensions
        this.extAndLocaleFunc();
        // run callbacks stored by this.ready
        await this.runCallbacks();
        window.addEventListener('message', this.messageListener.bind(this));

        const readyEvent = new Event("zaa-editor-ready");
        window.dispatchEvent(readyEvent);
    }

    setConfig = (newConfig: ISVGEditConfiguration, cfgCfg?: any): void => {
        throw new Error("Editor setConfig is not bound!")
    }

    private bindShortcuts() {
        // -- EDITOR --

        ShortcutManager.RegisterShortcut(
            [{ key: "n", ctrl: true, alt: true, }],
            () => this.createNewDocument(),
            { alias: "newdocument", actionName: this.i18next.t('tools.new_doc'), actionCategory: "Editor", actionDescription: "Clears the current canvas and creates a new blank document." }
        );
        ShortcutManager.RegisterShortcut(
            [{ key: "o", ctrl: true }],
            () => DotNet.invokeMethod(BLAZOR_ASSEMBLY, 'ShowOpenPanelDialog'),
            { alias: "opendocument", actionName: this.i18next.t('tools.open_doc'), actionCategory: "Editor", actionDescription: "Open the open panel dialog." }
        );
        ShortcutManager.RegisterShortcut(
            [{ key: "s", ctrl: true }],
            (e) => { e.preventDefault(); DotNet.invokeMethodAsync(BLAZOR_ASSEMBLY, 'SaveCurrentOrOpenDialog') },
            { alias: "savedocument", actionName: this.i18next.t('tools.save_doc'), actionCategory: "Editor", actionDescription: "Saves the current panel, or opens the save panel dialog if this panel hasn't been saved before." }
        );
        ShortcutManager.RegisterShortcut(
            [{ key: "s", ctrl: true, alt: true }],
            () => DotNet.invokeMethod(BLAZOR_ASSEMBLY, 'ShowSavePanelDialog'),
            { alias: "savedocumentas", actionName: this.i18next.t('tools.save_doc_as'), actionCategory: "Editor", actionDescription: "Opens the save panel dialog." }
        );
        ShortcutManager.RegisterShortcut(
            [{ key: "i", ctrl: true }],
            () => this.importSvgToNewLayer(),
            { alias: "importtolayer", actionName: this.i18next.t('tools.import_to_layer'), actionCategory: "Editor", actionDescription: "Import an SVG from your computer into the current artwork as a new layer." }
        );

        // -- SELECTION --

        ShortcutManager.RegisterShortcut(
            { key: "tab" },
            () => this.svgCanvas.cycleElement(0),
            { actionName: "Select Next Element", actionCategory: shortcutCategories.selection, actionDescription: "Selects the next element in the document on the currently selected layer." }
        );
        ShortcutManager.RegisterShortcut(
            { key: "tab", shift: true },
            () => this.svgCanvas.cycleElement(1),
            { actionName: "Select Previous Element", actionCategory: shortcutCategories.selection, actionDescription: "Selects the previous element in the document on the currently selected layer." }
        );
        ShortcutManager.RegisterShortcut(
            { key: "a", ctrl: true },
            () => this.svgCanvas.selectAllInCurrentLayer(),
            { actionName: "Select All in Current Layer", actionCategory: shortcutCategories.selection, actionDescription: "Selects all elements in the currently selected layer." }
        );
        ShortcutManager.RegisterShortcut(
            { key: "x", ctrl: true },
            () => this.cutSelected(),
            { alias: "cut", actionName: "Cut", actionCategory: shortcutCategories.selection, actionDescription: "Cuts the selected elements." }
        );
        ShortcutManager.RegisterShortcut(
            { key: "c", ctrl: true },
            () => this.copySelected(),
            { alias: "copy", actionName: "Copy", actionCategory: shortcutCategories.selection, actionDescription: "Copies the selected elements." }
        );
        ShortcutManager.RegisterShortcut(
            { key: "v", ctrl: true },
            () => this.pasteInCenter(),
            { alias: "paste", actionName: "Paste in Center", actionCategory: shortcutCategories.selection, actionDescription: "Pastes previously cut/copy elements in the center of the canvas." }
        );
        ShortcutManager.RegisterShortcut(
            [{ key: "v", ctrl: true, shift: true }, { key: "b", ctrl: true, shift: true }],
            () => this.svgCanvas.pasteElements('in_place'),
            { alias: "pasteinplace", actionName: "Paste in Place", actionCategory: shortcutCategories.selection, actionDescription: "Pastes previously cut/copy elements in their original locations." }
        );
        ShortcutManager.RegisterShortcut(
            [{ key: "g" }],
            () => {
                this.svgCanvas.groupUngroupSelectedElements();
            },
            { alias: "group", actionName: "Group/Ungroup Selected Elements", actionCategory: shortcutCategories.selection, actionDescription: "Groups the selected elements, or if the selection is already an element group, ungroups them." }
        );
        ShortcutManager.RegisterShortcut(
            [{ key: "d" }],
            () => {
                this.svgCanvas.cloneSelectedElements(20, 20);
            },
            { alias: "duplicate", actionName: "Duplicate Selected Elements", actionCategory: shortcutCategories.selection, actionDescription: "Duplicates the currently selected elements." }
        );

        ShortcutManager.RegisterShortcut(
            { key: "]", ctrl: true },
            () => this.moveUpDownSelected('Up'),
            { alias: "bringforward", actionName: "Bring Selected Forward", actionCategory: shortcutCategories.selection, actionDescription: "Brings the selected elements upwards on the current layer's element stack. This may put this element in front of some other elements." }
        );
        ShortcutManager.RegisterShortcut(
            { key: "[", ctrl: true },
            () => this.moveUpDownSelected('Down'),
            { alias: "pushbackward", actionName: "Push Selected Backwards", actionCategory: shortcutCategories.selection, actionDescription: "Pushes the selected elements downwards on the current layer's element stack. This may put the element behind some other elements." }
        );
        ShortcutManager.RegisterShortcut(
            { key: "]", ctrl: true, shift: true },
            () => this.svgCanvas.moveToTopSelectedElement(),
            { alias: "bringtofront", actionName: "Bring Selected to Front", actionCategory: shortcutCategories.selection, actionDescription: "Brings the selected elements to the top of the current layer's element stack, placing them in front of all other elements." }
        );
        ShortcutManager.RegisterShortcut(
            { key: "[", ctrl: true, shift: true },
            () => this.svgCanvas.moveToBottomSelectedElement(),
            { alias: "pushtobottom", actionName: "Push Selected to Bottom", actionCategory: shortcutCategories.selection, actionDescription: "Pushes the selected elements to the bottom of the current layer's element stack, placing them behind all other elements." }
        );

        ShortcutManager.RegisterShortcut(
            [{ key: "delete" }, { key: "backspace" }],
            () => this.svgCanvas.deleteSelectedElements(),
            { alias: "delete", actionName: "Delete Selected Elements", actionCategory: shortcutCategories.selection, actionDescription: "Deletes the selected elements from the canvas." }
        );

        const moveDirs = [
            { dir: "Up", x: 0, y: -1 },
            { dir: "Down", x: 0, y: 1 },
            { dir: "Left", x: -1, y: 0 },
            { dir: "Right", x: 1, y: 0 }
        ]

        for (const md of moveDirs) {
            ShortcutManager.RegisterShortcut(
                { key: `arrow${md.dir.toLowerCase()}`, },
                () => this.moveSelected(md.x, md.y),
                { actionName: `Move ${md.dir}`, actionCategory: shortcutCategories.selection, actionDescription: `Moves the selected elements ${md.dir.toLowerCase()} one unit.` }
            );

            ShortcutManager.RegisterShortcut(
                { key: `arrow${md.dir.toLowerCase()}`, shift: true },
                () => this.moveSelected(md.x * 10.0, md.y * 10.0),
                { actionName: `Move ${md.dir} (Large Distance)`, actionCategory: shortcutCategories.selection, actionDescription: `Moves the selected elements ${md.dir.toLowerCase()} ten units.` }
            );

            ShortcutManager.RegisterShortcut(
                { key: `arrow${md.dir.toLowerCase()}`, alt: true, },
                () => this.svgCanvas.cloneSelectedElements(md.x, md.y),
                { actionName: `Clone ${md.dir}`, actionCategory: shortcutCategories.selection, actionDescription: `Clones the selected elements and moves the clones ${md.dir.toLowerCase()} one unit.` }
            );

            ShortcutManager.RegisterShortcut(
                { key: `arrow${md.dir.toLowerCase()}`, alt: true, shift: true },
                () => this.svgCanvas.cloneSelectedElements(md.x * 10.0, md.y * 10.0),
                { actionName: `Clone ${md.dir} (Large Distance)`, actionCategory: shortcutCategories.selection, actionDescription: `Clones the selected elements and moves the clones ${md.dir.toLowerCase()} ten units.` }
            );
        }

        ShortcutManager.RegisterShortcut(
            { key: "arrowleft", ctrl: true },
            () => this.rotateSelected(0, 1),
            { actionName: "Rotate Left 1 Degree", actionCategory: shortcutCategories.selection, actionDescription: "Rotates the selected elements anticlockwise by 1 degree." }
        );
        ShortcutManager.RegisterShortcut(
            { key: "arrowright", ctrl: true },
            () => this.rotateSelected(1, 1),
            { actionName: "Rotate Right 1 Degree", actionCategory: shortcutCategories.selection, actionDescription: "Rotates the selected elements clockwise by 1 degree." }
        );
        ShortcutManager.RegisterShortcut(
            { key: "arrowleft", ctrl: true, shift: true },
            () => this.rotateSelected(0, 5),
            { actionName: "Rotate Left 5 Degrees", actionCategory: shortcutCategories.selection, actionDescription: "Rotates the selected elements anticlockwise by 5 degrees." }
        );
        ShortcutManager.RegisterShortcut(
            { key: "arrowright", ctrl: true, shift: true },
            () => this.rotateSelected(1, 5),
            { actionName: "Rotate Right 5 Degrees", actionCategory: shortcutCategories.selection, actionDescription: "Rotates the selected elements clockwise by 5 degrees." }
        );

        // -- ZOOM --

        ShortcutManager.RegisterShortcut(
            [{ key: "=", ctrl: true }, { key: "arrowup", ctrl: true }],
            () => this.zoomImage(1.2),
            { actionName: "Zoom In", actionCategory: shortcutCategories.zoom, actionDescription: "Zooms the canvas in." });
        ShortcutManager.RegisterShortcut(
            [{ key: "-", ctrl: true },
            { key: "arrowdown", ctrl: true }],
            () => this.zoomImage(1 / 1.2),
            { actionName: "Zoom Out", actionCategory: shortcutCategories.zoom, actionDescription: "Zooms the canvas out." });
        ShortcutManager.RegisterShortcut(
            [{ key: "0", ctrl: true }],
            () => this.zoomChanged("canvas"),
            { actionName: "Fit Canvas to Window", actionCategory: shortcutCategories.zoom, actionDescription: "Zooms so that the entire canvas is visible." });

        // -- CANVAS --

        ShortcutManager.RegisterShortcut(
            [{ key: "r", ctrl: true }],
            () => {
                this.configObj.pref("showRulers", !this.configObj.getBoolPref("showRulers", true));
                this.rulers.updateRulers();
            },
            { alias: "togglerulers", actionName: "Show/Hide Rulers", actionCategory: shortcutCategories.canvas, actionDescription: "Toggles the display of the canvas rulers." }
        );
        ShortcutManager.RegisterShortcut(
            [{ key: "'", ctrl: true }],
            () => {
                $id("view_grid").click();
            },
            { alias: "togglegrid", actionName: "Show/Hide Grid", actionCategory: shortcutCategories.canvas, actionDescription: "Toggles the display of the canvas grid." }
        );
        ShortcutManager.RegisterShortcut(
            [{ key: "'", ctrl: true, shift: true }],
            () => {
                $id("grid_snapping").click();
            },
            { alias: "togglegridsnap", actionName: "Toggle Grid Snapping", actionCategory: shortcutCategories.canvas, actionDescription: "Toggles whether elements snap to the grid or not." }
        );

        ShortcutManager.RegisterShortcut(
            [{ key: "u" }],
            () => {
                this.showSourceEditor();
            },
            { alias: "showsource", actionName: "Show SVG Source Editor", actionCategory: shortcutCategories.canvas, actionDescription: "Shows the SVG source for the current panel." }
        );

        ShortcutManager.RegisterShortcut(
            [{ key: "f" }],
            () => {
                this.toggleWireframe();
            },
            { alias: "togglewireframe", actionName: "Toggle Outline/Wireframe Mode", actionCategory: shortcutCategories.canvas, actionDescription: "Toggles outline/wireframe mode for the canvas." }
        );


        // -- UNDO/REDO --

        ShortcutManager.RegisterShortcut(
            [{ key: "z", ctrl: true }],
            () => this.svgCanvas.undoMgr.undo(),
            { alias: "undo", actionName: "Undo", actionCategory: shortcutCategories.undoRedo, actionDescription: "Undoes the last operation." }
        );
        ShortcutManager.RegisterShortcut(
            [{ key: "z", ctrl: true, shift: true }, { key: "y", ctrl: true }],
            () => this.svgCanvas.undoMgr.redo(),
            { alias: "redo", actionName: "Redo", actionCategory: shortcutCategories.undoRedo, actionDescription: "Re-applies the last undone operation." }
        );
    }

    /**
     *
     * @param {string} str SVG string
     * @param {PlainObject} [opts={}]
     * @param {boolean} [opts.noAlert]
     * @throws {Error} Upon failure to load SVG
     * @returns {void}
     */
    loadSvgString(str: string, { noAlert }: any = {}) { // TODO: Fix as any
        this.svgCanvas.clear();
        const success = this.svgCanvas.setSvgString(str, true) !== false;
        if (success) {
            this.updateCanvas();
            return;
        }

        if (!noAlert) {
            seAlert(this.i18next.t('notification.errorLoadingSVG'));
        }

        throw new Error('Error loading SVG');
    }

    checkIfContentEditableHasFocus(target: HTMLElement, eventKey: string) {
        if (document.activeElement?.getAttribute('contenteditable') === 'true') {
            console.log('ShortcutManager: Skipping - contenteditable has focus, key:', eventKey);
            return true;
        }

        // Also check if any ancestor is contenteditable
        let currentElement = target as Element | null;
        while (currentElement && currentElement !== document.body) {
            if (currentElement.getAttribute('contenteditable') === 'true') {
                console.log('ShortcutManager: Skipping - ancestor is contenteditable, key:', eventKey);
                return true;
            }
            currentElement = currentElement.parentElement;
        }

        return false;
    }

    /**
    * @function module:SVGthis.loadFromString
    * @param {string} str The SVG string to load
    * @param {PlainObject} [opts={}]
    * @param {boolean} [opts.noAlert=false] Option to avoid alert to user and instead get rejected promise
    * @returns {Promise<void>}
    */
    loadFromString(str: string, { noAlert }: any = {}) { // TODO: Fix as any
        return this.ready(() => {
            try {
                this.loadSvgString(str, { noAlert });
            } catch (err) {
                if (noAlert) {
                    throw err;
                }
            }
        });
    }

    setCurrentFileUrl(url: string) {
        this.currentFileUrl = url;
    }

    getCurrentFileUrl() {
        return this.currentFileUrl;
    }

    getCurrentFileDirectoryUrl() {
        const match = (this.currentFileUrl ?? "").match(/(.*)[/\\]/);
        if (match && match.length > 1) {
            return match[1] + "/";
        }
        return "";
    }

    /**
    * @callback module:SVGthis.URLLoadCallback
    * @param {boolean} success
    * @returns {void}
    */
    /**
   * @function module:SVGthis.loadFromURL
   * @param {string} url URL from which to load an SVG string via Ajax
   * @param {PlainObject} [opts={}] May contain properties: `cache`, `callback`
   * @param {boolean} [opts.cache]
   * @param {boolean} [opts.noAlert]
   * @returns {Promise<void>} Resolves to `undefined` or rejects upon bad loading of
   *   the SVG (or upon failure to parse the loaded string) when `noAlert` is
   *   enabled
   */
    loadFromURL(url: string, { cache, noAlert }: any = {}) { // TODO: Fix as any
        return this.ready(() => {
            return new Promise<void>((resolve, reject) => {
                $.ajax({
                    url,
                    dataType: 'text',
                    cache: Boolean(cache),
                    beforeSend() {
                        $.process_cancel(this.i18next.t('notification.loadingImage'));
                    },
                    success(str) {
                        this.loadSvgString(str, { noAlert });
                    },
                    error(xhr, stat, err) {
                        if (xhr.status !== 404 && xhr.responseText) {
                            this.loadSvgString(xhr.responseText, { noAlert });
                            return;
                        }
                        if (noAlert) {
                            reject(new Error('URLLoadFail'));
                            return;
                        }
                        seAlert(this.i18next.t('notification.URLLoadFail') + ': \n' + err);
                        resolve();
                    },
                    complete() {
                        if ($id("dialog_box") != null) $id("dialog_box").style.display = 'none';
                    }
                });
            });
        });
    }

    /**
    * @function module:SVGthis.loadFromDataURI
    * @param {string} str The Data URI to base64-decode (if relevant) and load
    * @param {PlainObject} [opts={}]
    * @param {boolean} [opts.noAlert]
    * @returns {Promise<void>} Resolves to `undefined` and rejects if loading SVG string fails and `noAlert` is enabled
    */
    loadFromDataURI(str: string, { noAlert }: any = {}) { // TODO: Fix as any
        return this.ready(() => {
            let base64 = false;
            let pre: RegExpMatchArray | string = str.match(/^data:image\/svg\+xml;base64,/);
            if (pre) {
                base64 = true;
            } else {
                pre = str.match(/^data:image\/svg\+xml(?:;|;utf8)?,/);
            }
            if (pre) {
                pre = pre[0];
            }
            const src = str.slice(pre.length);
            return this.loadSvgString(base64 ? decode64(src) : decodeURIComponent(src), { noAlert });
        });
    }

    /**
    * @function module:SVGthis.updateCanvas
    * @param {boolean} [center]
    * @param {module:math.XYObject} [newCtr]
    * @returns {void}
    */
    updateCanvas(center?: boolean, newCtr?: XYObject) {
        const zoom = this.svgCanvas.getCurrentZoom();
        const wArea = this.workarea;
        const cnvs = $id("svgcanvas");
        const workareaStyle = getComputedStyle(this.workarea, null);
        const canvasStyle = getComputedStyle(cnvs, null);

        let w = 0;
        let h = 0;

        try {
            w = parseFloat(workareaStyle.width.replace("px", ""));
        } catch (e) {
            console.error(e);
        }
        try {
            h = parseFloat(workareaStyle.height.replace("px", ""));
        } catch (e) {
            console.error(e);
        }

        const wOrig = w, hOrig = h;
        const oldCtr = {
            x: wArea.scrollLeft + wOrig / 2,
            y: wArea.scrollTop + hOrig / 2
        };
        const multi = this.configObj.curConfig.canvas_expansion;
        w = Math.max(wOrig, this.svgCanvas.contentW * zoom * multi);
        h = Math.max(hOrig, this.svgCanvas.contentH * zoom * multi);

        if (w === wOrig && h === hOrig) {
            this.workarea.style.overflow = 'hidden';
        } else {
            this.workarea.style.overflow = 'scroll';
        }

        const oldCanY = parseFloat(canvasStyle.height.replace("px", "")) / 2;
        const oldCanX = parseFloat(canvasStyle.width.replace("px", "")) / 2;

        cnvs.style.width = w + "px";
        cnvs.style.height = h + "px";
        const newCanY = h / 2;
        const newCanX = w / 2;
        const offset = this.svgCanvas.updateCanvas(w, h);

        const ratio = newCanX / oldCanX;

        const scrollX = w / 2 - wOrig / 2;
        const scrollY = h / 2 - hOrig / 2;

        if (!newCtr) {
            const oldDistX = oldCtr.x - oldCanX;
            const newX = newCanX + oldDistX * ratio;

            const oldDistY = oldCtr.y - oldCanY;
            const newY = newCanY + oldDistY * ratio;

            newCtr = {
                x: newX,
                y: newY
            };
        } else {
            newCtr.x += offset.x;
            newCtr.y += offset.y;
        }

        if (center) {
            // Go to top-left for larger documents
            if (this.svgCanvas.contentW > parseFloat(getComputedStyle(wArea, null).width.replace("px", ""))) {
                // Top-left
                this.workarea.scrollLeft = offset.x - 10;
                this.workarea.scrollTop = offset.y - 10;
            } else {
                // Center
                wArea.scrollLeft = scrollX;
                wArea.scrollTop = scrollY;
            }
        } else {
            wArea.scrollLeft = newCtr.x - wOrig / 2;
            wArea.scrollTop = newCtr.y - hOrig / 2;
        }
        if (this.configObj.getBoolPref("showRulers", true)) {
            this.rulers.updateRulers(cnvs, zoom);
            this.workarea.scroll();
        }

    }

    // called when we've selected a different element
    /**
    *
    * @param {external:Window} win
    * @param {module:svgcanvas.SvgCanvas#event:selected} elems Array of elements that were selected
    * @listens module:svgcanvas.SvgCanvas#event:selected
    * @fires module:svgcanvas.SvgCanvas#event:ext_selectedChanged
    * @returns {void}
    */
    selectedChanged(win, elems) {
        const actionableElems = elems.filter(el => el && !isElementEffectivelyLocked(el));
        const mode = this.svgCanvas.getMode();
        const isNode = mode === 'pathedit';
        // if this.elems[1] is present, then we have more than one element
        this.selectedElement = (actionableElems.length === 1 ? actionableElems[0] : null);
        this.multiselected = (actionableElems.length >= 2);
        if (!isNullish(this.selectedElement) && !isNode) {
            this.topPanel.update();
        } // if (!isNullish(elem))

        // Deal with pathedit mode
        this.togglePathEditMode(isNode, elems);
        this.topPanel.updateContextPanel();
        this.svgCanvas.runExtensions('selectedChanged', /** @type {module:svgcanvas.SvgCanvas#event:ext_selectedChanged} */ {
            elems,
            selectedElement: this.selectedElement,
            multiselected: this.multiselected
        });
    }

    // Call when part of element is in process of changing, generally
    // on mousemove actions like rotate, move, etc.
    /**
     * @param {external:Window} win
     * @param {module:svgcanvas.SvgCanvas#event:transition} elems
     * @listens module:svgcanvas.SvgCanvas#event:transition
     * @fires module:svgcanvas.SvgCanvas#event:ext_elementTransition
     * @returns {void}
     */
    elementTransition(win, elems) {
        const mode = this.svgCanvas.getMode();
        const elem = elems[0];

        if (!elem) {
            return;
        }

        this.multiselected = (elems.length >= 2 && !isNullish(elems[1]));
        // Only updating fields for single elements for now
        if (!this.multiselected) {
            // eslint-disable-next-line sonarjs/no-small-switch
            switch (mode) {
                case 'rotate': {
                    const ang = this.svgCanvas.getRotationAngle(elem);
                    $id<HTMLInputElement>('angle').value = ang.toString();
                    (ang === 0) ? $id('tool_reorient').classList.add('disabled') : $id('tool_reorient').classList.remove('disabled');
                    break;
                }
            }
        }
        this.svgCanvas.runExtensions('elementTransition', /** @type {module:svgcanvas.SvgCanvas#event:ext_elementTransition} */ {
            elems
        });
    }

    // called when any element has changed
    /**
     * @param {external:Window} win
     * @param {Array<PlainObject>} elems
     * @listens module:svgcanvas.SvgCanvas#event:changed
     * @fires module:svgcanvas.SvgCanvas#event:ext_elementChanged
     * @returns {void}
     */
    elementChanged(win, elems) {
        elems.forEach((elem) => {
            const isSvgElem = (elem && elem.tagName === 'svg');
            if (isSvgElem || this.svgCanvas.isLayer(elem)) {
                // if the element changed was the svg, then it could be a resolution change
                if (isSvgElem) {
                    this.updateCanvas();
                }
                // Update selectedElement if element is no longer part of the image.
                // This occurs for the text elements in Firefox
            } else if (elem && this.selectedElement && isNullish(this.selectedElement.parentNode)) {
                // || elem && elem.tagName == "path" && !multiselected) { // This was added in r1430, but not sure why
                this.selectedElement = elem;
            }
        });

        this.showSaveWarning = true;

        // we update the contextual panel with potentially new
        // positional/sizing information (we DON'T want to update the
        // toolbar here as that creates an infinite loop)
        // also this updates the history buttons

        // we tell it to skip focusing the text control if the
        // text element was previously in focus
        this.topPanel.updateContextPanel();

        this.svgCanvas.runExtensions('elementChanged', /** @type {module:svgcanvas.SvgCanvas#event:ext_elementChanged} */ {
            elems
        });

        const ev = new CustomEvent("element-state-changed", { detail: elems });
        window.dispatchEvent(ev);
    }

    /**
*
* @returns {void}
*/
    cutSelected() {
        if (!isNullish(this.selectedElement) || this.multiselected) {
            this.svgCanvas.cutSelectedElements();
        }
    }

    /**
    * @function copySelected
    * @returns {void}
    */
    copySelected() {
        if (!isNullish(this.selectedElement) || this.multiselected) {
            this.svgCanvas.copySelectedElements();
        }
    }

    /**
    *
    * @returns {void}
    */
    pasteInCenter() {
        const zoom = this.svgCanvas.getCurrentZoom();
        const x = (this.workarea.scrollLeft + parseFloat(getComputedStyle(this.workarea, null).width.replace("px", "")) / 2) / zoom - this.svgCanvas.contentW;
        const y = (this.workarea.scrollTop + parseFloat(getComputedStyle(this.workarea, null).height.replace("px", "")) / 2) / zoom - this.svgCanvas.contentH;
        this.svgCanvas.pasteElements('point', x, y);
    }

    /**
    * @param {"Up"|"Down"} dir
    * @returns {void}
    */
    moveUpDownSelected(dir) {
        if (!isNullish(this.selectedElement)) {
            this.svgCanvas.moveUpDownSelected(dir);
        }
    }

    /**
    * @param {Float} dx
    * @param {Float} dy
    * @returns {void}
    */
    moveSelected(dx, dy) {
        if (!isNullish(this.selectedElement) || this.multiselected) {
            if (this.configObj.curConfig.gridSnapping) {
                // Use grid snap value regardless of zoom level
                const multi = this.svgCanvas.getCurrentZoom() * this.configObj.curConfig.snappingStep;
                dx *= multi;
                dy *= multi;
            }
            this.svgCanvas.moveSelectedElements(dx, dy);
        }
    }

    /**
    *
    * @returns {void}
    */
    selectNext() {
        this.svgCanvas.cycleElement(1);
    }

    /**
    *
    * @returns {void}
    */
    selectPrev() {
        this.svgCanvas.cycleElement(0);
    }

    /**
    * @param {0|1} cw
    * @param {Integer} step
    * @returns {void}
    */
    rotateSelected(cw, step) {
        if (isNullish(this.selectedElement) || this.multiselected) { return; }
        if (!cw) { step *= -1; }
        const angle = Number.parseFloat($id<HTMLInputElement>('angle').value) + step;
        this.svgCanvas.setRotationAngle(angle);
        this.topPanel.updateContextPanel();
    }

    /**
     * @returns {void}
     */
    zoomDone() {
        this.updateWireFrame();
    }

    /**
    * @function module:svgcanvas.SvgCanvas#zoomChanged
    * @param {external:Window} win
    * @param {module:svgcanvas.SvgCanvas#event:zoomed} bbox
    * @param {boolean} autoCenter
    * @listens module:svgcanvas.SvgCanvas#event:zoomed
    * @returns {void}
    */
    zoomChanged(bbox: BoundingBoxZoomOption | number, autoCenter?: boolean) {
        const scrbar = 15;
        const wArea = this.workarea;

        if(typeof bbox === 'number') {
            let zoomlevel = Number(bbox);
            if (zoomlevel < 0.001) {
                zoomlevel = 0.001;
            }
            const zoom = this.svgCanvas.getCurrentZoom();

            bbox = {
                width: 0,
                height: 0,
                // center pt of scroll position
                x: (wArea.scrollLeft + parseFloat(getComputedStyle(wArea, null).width.replace("px", "")) / 2) / zoom,
                y: (wArea.scrollTop + parseFloat(getComputedStyle(wArea, null).height.replace("px", "")) / 2) / zoom,
                zoom: zoomlevel
            };
            autoCenter = true;
        }

        const zInfo = this.svgCanvas.setBBoxZoom(bbox, parseFloat(getComputedStyle(wArea, null).width.replace("px", "")) - scrbar, parseFloat(getComputedStyle(wArea, null).height.replace("px", "")) - scrbar);

        if (!zInfo) {
            return;
        }
        const zoomlevel = zInfo.zoom;
        const bb = zInfo.bbox;

        if (autoCenter) {
            this.updateCanvas();
        } else {
            this.updateCanvas(
                false,
                { x: bb.x * zoomlevel + (bb.width * zoomlevel) / 2, y: bb.y * zoomlevel + (bb.height * zoomlevel) / 2 }
            );
        }

        if (this.svgCanvas.getMode() === 'zoom' && bb.width) {
            // Go to select if a zoom box was drawn
            // this.svgCanvas.setMode("select");
        }

        const ev = new CustomEvent("zoom-complete", { detail: zInfo });
        window.dispatchEvent(ev);
        this.zoomDone();
    }

    /**
    * @param {Float} multiplier
    * @returns {void}
    */
    zoomImage(multiplier?: Float) {
        const resolution = this.svgCanvas.getResolution();
        multiplier = multiplier ? resolution.zoom * multiplier : 1;
        this.svgCanvas.setZoom(multiplier);
        this.zoomDone();
        this.updateCanvas(true);
    }

    /**
    * @function module:SVGEditor.setIcon
    * @param {string|Element|external:jQuery} elem
    * @param {string|external:jQuery} iconId
    * @returns {void}
    */
    setIcon(elem, iconId) {
        // eslint-disable-next-line max-len
        const img = document.createElement("img");
        img.src = this.configObj.curConfig.imgPath + iconId;
        const icon = (typeof iconId === 'string') ? img : iconId.cloneNode(true);
        if (!icon) {
            // Todo: Investigate why this still occurs in some cases
            console.log('NOTE: Icon image missing: ' + iconId);
            return;
        }
        // empty()
        while ($id(elem).firstChild)
            $id(elem).removeChild($id(elem).firstChild);
        $id(elem).appendChild(icon);
    }

    showSourceEditor(forSaving?: boolean) {
        DialogWrapper.GetDialog("svgSourceEditor").openToolDialog(document.getElementById("tool_tageditor"));
    }

    wireframeActive: boolean = false;
    toggleWireframe() {
        this.workarea.classList.toggle("wireframe");

        const wfRules = $id("wireframe_rules");
        if (!wfRules) {
            const fcRules = document.createElement('style');
            fcRules.setAttribute('id', 'wireframe_rules');
            document.getElementsByTagName("head")[0].appendChild(fcRules);
        } else {
            while (wfRules.firstChild)
                wfRules.removeChild(wfRules.firstChild);
        }
        this.updateWireFrame();
        this.wireframeActive = !this.wireframeActive;
    }


    /**
    *
    * @returns {void}
    */
    updateWireFrame() {
        const rule = `
      #workarea.wireframe #svgcontent * {
        stroke-width: ${1 / this.svgCanvas.getCurrentZoom()}px;
      }
    `;
        if (document.querySelectorAll("#wireframe_rules").length > 0) {
            document.querySelector("#wireframe_rules").textContent = (this.workarea.classList.contains('wireframe') ? rule : '');
        }
    }

    /**
    * @param {string} [title=svgCanvas.getDocumentTitle()]
    * @returns {void}
    */
    updateTitle(title?: string) {
        title = title || this.svgCanvas.getDocumentTitle();
        const newTitle = document.querySelector('title').text + (title ? ': ' + title : '');

        // Remove title update with current context info, isn't really necessary
        // if (this.curContext) {
        //   new_title = new_title + this.curContext;
        // }
        document.querySelector('title').textContent = newTitle;
    }


    /**
     * @param {PlainObject} opts
     * @returns {Promise<PlainObject>}
     */
    setCustomHandlers(opts) {
        return this.ready(() => {
            if (opts.open) {
                this.svgCanvas.open = opts.open.bind(this);
            }
            if (opts.save) {
                this.showSaveWarning = false;
                this.svgCanvas.bind('saved', opts.save.bind(this));
            }
            if (opts.exportImage) {
                this.customExportImage = opts.exportImage.bind(this) as ((...args: any[]) => any);
                this.svgCanvas.bind('exported', this.customExportImage); // canvg and our RGBColor will be available to the method
            }
            if (opts.exportPDF) {
                this.customExportPDF = opts.exportPDF.bind(this) as ((...args: any[]) => any);
                this.svgCanvas.bind('exportedPDF', this.customExportPDF); // jsPDF and our RGBColor will be available to the method
            }
        });
    }

    /**
     * @function module:SVGthis.randomizeIds
     * @param {boolean} arg
     * @returns {void}
     */
    randomizeIds(arg) {
        this.svgCanvas.randomizeIds(arg);
    }

    /** @lends module:SVGEditor~Actions */
    /**
    * @returns {void}
    */
    setAll() {
        // Make 'return' keypress trigger the change event
        const elements = document.getElementsByClassName("attr_changer");
        Array.from(elements).forEach(function (element) {
            element.addEventListener('keydown', function (evt) {
                evt.currentTarget.dispatchEvent(new Event('change'));
                evt.preventDefault();
            });
        });
        /*
        $id('image_url').addEventListener('keydown', function (evt) {
            evt.currentTarget.dispatchEvent(new Event('change'));
            evt.preventDefault();
        });
        */
    }
    // parents() https://stackoverflow.com/a/12981248
    getParents(el, parentSelector /* optional */) {

        // If no parentSelector defined will bubble up all the way to *document*
        if (parentSelector === undefined) {
            parentSelector = document;
        }

        let parents = [];
        let p = el.parentNode;

        while (p !== parentSelector) {
            const o = p;
            parents.push(o);
            p = o.parentNode;
        }
        parents.push(parentSelector); // Push that parentSelector you wanted to stop at

        return parents;
    }

    /**
    * @returns {void}
    */
    setTitles() {
        // Tooltips not directly associated with a single function
        const keyAssocs = {
            '4/Shift+4': 'tools_rect',
            '5/Shift+5': 'tools_ellipse'
        };
        Object.entries(keyAssocs).forEach(([keyval, sel]) => {
            const parentsElements = this.getParents($id(sel), $id('main_menu'))
            const menu = (parentsElements.length);

            $qa(sel).forEach((element) => {
                const t = (menu) ? element.textContent.split(' [')[0] : element.title.split(' [')[0];
                let keyStr = '';
                // Shift+Up
                keyval.split('/').forEach((key, i) => {
                    const modBits = key.split('+');
                    let mod = '';
                    if (modBits.length > 1) {
                        mod = modBits[0] + '+';
                        key = modBits[1];
                    }
                    keyStr += (i ? '/' : '') + mod + (this.uiStrings['key_' + key] || key);
                });
                if (menu) {
                    element.lastChild.textContent = t + ' [' + keyStr + ']';
                } else {
                    element.title = t + ' [' + keyStr + ']';
                }
            });
        });
    }

    /**
    * @param {string} sel Selector to match
    * @returns {module:SVGthis.ToolButton}
    */
    getButtonData(sel) {
        return Object.values(this.shortcuts).find((btn) => {
            return btn.sel === sel;
        });
    }

    /**
    * Expose the `uiStrings`.
    * @function module:SVGthis.canvas.getUIStrings
    * @returns {module:SVGthis.uiStrings}
    */
    getUIStrings() {
        return this.uiStrings;
    }

    public pathEditMode: boolean = false;
    /**
    * @param {boolean} editmode
    * @param {module:svgcanvas.SvgCanvas#event:selected} elems
    * @returns {void}
    */
    togglePathEditMode(editmode, elems) {
        this.pathEditMode = editmode;
        if (editmode) {
            // Change select icon
            const elements = document.getElementsByClassName("tool_button_current");
            Array.from(elements).forEach(function (element) {
                element.classList.add('tool_button_current');
                element.classList.remove('tool_button')
            });
            $id('tool_select').classList.add('tool_button_current')
            $id('tool_select').classList.remove('tool_button');
            this.multiselected = false;
            if (elems.length) {
                this.selectedElement = elems[0];
            }
        }
    }

    /**
     * @param {external:Window} win
     * @param {module:svgcanvas.SvgCanvas#event:exported} data
     * @listens module:svgcanvas.SvgCanvas#event:exported
     * @returns {void}
     */
    exportHandler(win, data) {
        const { issues, exportWindowName } = data;

        this.exportWindow = window.open(blankPageObjectURL || '', exportWindowName); // A hack to get the window via JSON-able name without opening a new one

        if (!this.exportWindow || this.exportWindow.closed) {
            seAlert(this.i18next.t('notification.popupWindowBlocked'));
            return;
        }

        this.exportWindow.location.href = data.bloburl || data.datauri;
        const done = this.configObj.pref('export_notice_done');
        if (done !== 'all') {
            let note = this.i18next.t('notification.saveFromBrowser', { type: data.type });

            // Check if there are issues
            if (issues.length) {
                const pre = '\n \u2022 ';
                note += ('\n\n' + this.i18next.t('notification..noteTheseIssues') + pre + issues.join(pre));
            }

            // Note that this will also prevent the notice even though new issues may appear later.
            // May want to find a way to deal with that without annoying the user
            this.configObj.pref('export_notice_done', 'all');
            seAlert(note);
        }
    }

    /**
    * Set a selected image's URL.
    * @function module:SVGthis.setImageURL
    * @param {string} url
    * @returns {void}
    */
    setImageURL(url) {
        if (!url) {
            url = this.defaultImageURL;
        }
        this.svgCanvas.setImageURL(url);
        $id<HTMLInputElement>("image_url").value = url;

        if (url.startsWith('data:')) {
            // data URI found
            $id("image_url").style.display = 'none';
            $id("change_image_url").style.display = 'block';
        } else {
            // regular URL
            this.defaultImageURL = url;
            void this.svgCanvas.embedImage(url)
                .then(dataURI => {
                    // Couldn't embed, so show warning
                    $id('url_notice').style.display = (!dataURI) ? 'block' : 'none';
                })
                .catch(error => {
                    $id('url_notice').style.display = 'block';
                    console.error(`Could not embed image from URL: ${url}`, error);
                });
            $id("image_url").style.display = 'block';
            $id("change_image_url").style.display = 'none';
        }
    }

    /**
     *
     * @param {string} color
     * @param {string} url
     * @returns {void}
     */
    setBackground(color: string, url?: string) {
        this.configObj.pref('bkgd_color', color);
        this.configObj.pref('bkgd_url', url, true);

        // This should be done in this.svgCanvas.ts for the borderRect fill
        this.svgCanvas.setBackground(color, url);
        window.localStorage.setItem("bkgd_color", color);
        window.localStorage.setItem("bkgd_url", url);
    }

    /**
    * @function module:SVGthis.addExtension
    * @param {string} name Used internally; no need for i18n.
    * @param {module:svgcanvas.ExtensionInitCallback} initfn Config to be invoked on this module
    * @param {module:svgcanvas.ExtensionInitArgs} initArgs
    * @throws {Error} If called too early
    * @returns {Promise<void>} Resolves to `undefined`
    */
    addExtension(name: string, initfn, initArgs) {
        // Note that we don't want this on this.ready since some extensions
        // may want to run before then (like server_opensave).
        if (!this.svgCanvas) {
            throw new Error('Extension added too early');
        }
        return this.svgCanvas.addExtension.call(this, name, initfn, initArgs);
    }


    /**
     * @param {external:Window} win
     * @param {module:svgcanvas.SvgCanvas#event:extension_added} ext
     * @listens module:svgcanvas.SvgCanvas#event:extension_added
     * @returns {Promise<void>|void} Resolves to `undefined`
     */
    async extAdded(win, ext) {

        if (!ext) {
            return undefined;
        }
        let cbCalled = false;

        /**
        *
        * @returns {void}
        */
        const runCallback = () => {
            if (ext.callback && !cbCalled) {
                cbCalled = true;
                ext.callback.call(this);
            }
        };

        if (ext.events) {
            throw new Error("CANNOT ADD EXTENSION EVENTS - LEFT PANEL NO LONGER SUPPORTS add()");
        }
        return runCallback();
    }

    /**
     * @fires module:svgcanvas.SvgCanvas#event:ext_addLangData
     * @fires module:svgcanvas.SvgCanvas#event:ext_langReady
     * @fires module:svgcanvas.SvgCanvas#event:ext_langChanged
     * @fires module:svgcanvas.SvgCanvas#event:extensions_added
     * @returns {Promise<module:locale.LangAndData>} Resolves to result of {@link module:locale.readLang}
     */
    async extAndLocaleFunc() {
        this.$svgEditor.style.visibility = 'visible';
        try {
            // Initialize extensions included in the main bundle.
            await Promise.all(
                builtInExtensions.map(({ name, init }) => {
                    return this.addExtension(name, init?.bind(this), { $, langParam: 'en' }); /** @todo  change to current lng */
                })
            );
            // load user extensions (given as pathNames)
            await Promise.all(
                this.configObj.curConfig.userExtensions.map(async (extPathName) => {
                    /**
                     * @tutorial ExtensionDocs
                     * @typedef {PlainObject} module:SVGthis.ExtensionObject
                     * @property {string} [name] Name of the extension. Used internally; no need for i18n. Defaults to extension name without beginning "ext-" or ending ".js".
                     * @property {module:svgcanvas.ExtensionInitCallback} [init]
                     */
                    try {
                        /**
                         * @type {module:SVGthis.ExtensionObject}
                         */
                        // eslint-disable-next-line no-unsanitized/method
                        const imported = await import(encodeURI(extPathName));
                        const { name, init: initfn } = imported.default;
                        return this.addExtension(name, initfn?.bind(this), { $ });
                    } catch (err) {
                        // Todo: Add config to alert any errors
                        console.error('Extension failed to load: ' + extPathName + '; ', err);
                        return undefined;
                    }
                })
            );
            this.svgCanvas.bind(
                'extensions_added',
                /**
                * @param {external:Window} _win
                * @param {module:svgcanvas.SvgCanvas#event:extensions_added} _data
                * @listens module:SvgCanvas#event:extensions_added
                * @returns {void}
                */
                (_win, _data) => {
                    this.extensionsAdded = true;
                    this.setAll();
                    this.updateCanvas(true);

                    this.messageQueue.forEach(
                        /**
                         * @param {module:svgcanvas.SvgCanvas#event:message} messageObj
                         * @fires module:svgcanvas.SvgCanvas#event:message
                         * @returns {void}
                         */
                        (messageObj) => {
                            this.svgCanvas.call('message', messageObj);
                        }
                    );
                }
            );
            this.svgCanvas.call('extensions_added');
        } catch (err) {
            // Todo: Report errors through the UI
            console.log(err);
        }
    }

    /**
   * @param {PlainObject} info
   * @param {any} info.data
   * @param {string} info.origin
   * @fires module:svgcanvas.SvgCanvas#event:message
   * @returns {void}
   */
    messageListener({ data, origin }) {
        const messageObj = { data, origin };
        if (!this.extensionsAdded) {
            this.messageQueue.push(messageObj);
        } else {
            // Extensions can handle messages at this stage with their own
            //  canvas `message` listeners
            this.svgCanvas.call('message', messageObj);
        }
    }

    setPanning(active: boolean) {
        this.svgCanvas.inputState.spaceKeyDown = this.keypan = active;
    }

    async createNewDocument() {
        const [x, y] = this.configObj.curConfig.dimensions;
        const ok = await seConfirm(this.i18next.t('notification.QwantToClear'));
        if (ok === "Cancel") {
            return false;
        }

        this.svgCanvas.setMode("select");
        this.svgCanvas.clear();
        this.svgCanvas.setResolution(x, y);
        this.updateCanvas(true);
        this.zoomImage();
        this.topPanel.updateContextPanel();
        this.svgCanvas.runExtensions("onNewDocument");
        return true;
    }

    /**
     * @function module:SVGthis.openPrep
     * @returns {boolean|Promise<boolean>} Resolves to boolean indicating `true` if there were no changes
     *  and `false` after the user confirms.
     */
    async openPrep() {
        if (this.svgCanvas.undoMgr.getUndoStackSize() === 0) {
            return true;
        }
        return await seConfirm(this.i18next.t('notification.QwantToOpen'));
    }

    importSvgToNewLayer() {
        let input = document.getElementById(IMPORT_ARTWORK_INPUT_ID) as HTMLInputElement;

        if (!input) {
            input = document.createElement("input");
            input.id = IMPORT_ARTWORK_INPUT_ID;
            input.className = "visually-hidden";
            input.type = "file";
            input.accept = ".svg, image/svg+xml";

            input.addEventListener("change", (e) => {
                const file = (e.target as HTMLInputElement).files[0];
                console.log(`Importing SVG '${file.name}' to layer.`);

                const reader = new FileReader();
                reader.readAsText(file, "UTF-8");
                reader.addEventListener("load", (readerEvent) => {
                    (async () => {
                        console.log(`Preparing SVG resolution...`);

                        let autoLayerName = file.name;
                        if (autoLayerName.toLowerCase().endsWith(".svg")) {
                            autoLayerName = autoLayerName.substring(0, autoLayerName.length - 4);
                        }

                        const resolverDialog = DialogWrapper.GetDialog<ImportSvgResolverDialog>("importSvgResolver");
                        const result = await resolverDialog.openDialog(readerEvent.target.result, "Import", autoLayerName);

                        if (!result) {
                            console.log("SVG import cancelled at resolution stage");
                            return;
                        }

                        console.log(`Beginning SVG import...`);

                        let layerName = result.layerName;
                        let layerSuffix = 1;
                        while (this.svgCanvas.getCurrentDrawing().hasLayer(layerName)) {
                            layerName = `${result.layerName} ${++layerSuffix}`;
                        }

                        const destLayer = this.svgCanvas.createLayer(layerName);
                        destLayer.innerHTML = result.svg;

                        this.updateCanvas();
                        this.refreshUI();

                        console.log(`SVG '${file.name}' has been imported to layer '${layerName}'.`);
                    })();
                });
            });

            // We need to append it to the document for Mac reasons.
            document.body.appendChild(input);
        }

        input.click();
    }

    public refreshUI = () => { /* Stubbed by SvgEditService */ };


    /**
     *
     * @param {Event} e
     * @returns {void}
     */
    // eslint-disable-next-line class-methods-use-this
    onDragEnter(e) {
        e.stopPropagation();
        e.preventDefault();
        // and indicator should be displayed here, such as "drop files here"
    }

    /**
     *
     * @param {Event} e
     * @returns {void}
     */
    // eslint-disable-next-line class-methods-use-this
    onDragOver(e) {
        e.stopPropagation();
        e.preventDefault();
    }

    /**
     *
     * @param {Event} e
     * @returns {void}
     */
    // eslint-disable-next-line class-methods-use-this
    onDragLeave(e) {
        e.stopPropagation();
        e.preventDefault();
        // hypothetical indicator should be removed here
    }

    /**
    * @function module:SVGthis.setLang
    * @param {string} lang The language code
    * @param {module:locale.LocaleStrings} allStrings See {@tutorial LocaleDocs}
    * @fires module:svgcanvas.SvgCanvas#event:ext_langReady
    * @fires module:svgcanvas.SvgCanvas#event:ext_langChanged
    * @returns {void} A Promise which resolves to `undefined`
    */
    setLang(lang) {
        this.langChanged = true;
        this.configObj.pref('lang', lang);
        const $editDialog = document.getElementById('se-edit-prefs');
        $editDialog.setAttribute('lang', lang);
        const oldLayerName = ($id('#layerlist')) ? $id('#layerlist').querySelector('tr.layersel td.layername').textContent : "";
        const renameLayer = (oldLayerName === this.i18next.t('notification.common.layer') + ' 1');

        this.setTitles();

        if (renameLayer) {
            this.svgCanvas.renameCurrentLayer(this.i18next.t('notification.common.layer') + ' 1');
        }

        this.svgCanvas.runExtensions('langChanged', /** @type {module:svgcanvas.SvgCanvas#event:ext_langChanged} */ lang);
    }

    /**
  * @callback module:SVGthis.ReadyCallback
  * @returns {Promise<void>|void}
  */
    /**
  * Queues a callback to be invoked when the editor is ready (or
  *   to be invoked immediately if it is already ready--i.e.,
  *   if `runCallbacks` has been run).
  * @function module:SVGthis.ready
  * @param {module:SVGthis.ReadyCallback} cb Callback to be queued to invoke
  * @returns {Promise<ArbitraryCallbackResult>} Resolves when all callbacks, including the supplied have resolved
  */
    ready(cb) {
        return new Promise((resolve, reject) => {
            if (this.isReady) {
                resolve(cb());
                return;
            }
            this.callbacks.push([cb, resolve, reject]);
        });
    }

    /**
  * Invokes the callbacks previous set by `svgthis.ready`
  * @function module:SVGthis.runCallbacks
  * @returns {Promise<void>} Resolves to `undefined` if all callbacks succeeded and rejects otherwise
  */
    async runCallbacks() {
        try {
            await Promise.all(this.callbacks.map(([cb]) => {
                return cb();
            }));
        } catch (err) {
            this.callbacks.forEach(([, , reject]) => {
                reject();
            });
            throw err;
        }
        this.callbacks.forEach(([, resolve]) => {
            resolve();
        });
        this.isReady = true;
    }

    async loadSvgWithResolver(svgString: string) {
        console.log(`Preparing SVG resolution...`);

        const resolverDialog = DialogWrapper.GetDialog<ImportSvgResolverDialog>("importSvgResolver");
        const resolutionResult = await resolverDialog.openDialog(svgString, "Open");

        if (!resolutionResult) {
            console.log("SVG open cancelled at resolution stage");
            return;
        }

        console.log(`Beginning SVG open...`);

        svgEditServiceInstance.canvas.clear();
        svgEditServiceInstance.editor.loadSvgString(resolutionResult.svg);
        panelsServiceInstance.SetLastOpened(-1, true);
        svgEditServiceInstance.fixupExternalElementReferences();

        console.log(`SVG is now open.`);
        const ev = new CustomEvent("svgcontent-loaded-from-computer");
        window.dispatchEvent(ev);
    }
}